import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { hasParentWithSelector } from '@mm/utils/src/dom-utils';

type Props = {
  onClickInside?: (e?: React.MouseEvent<HTMLDivElement>) => any;
  handleClick: (
    e?: React.MouseEvent<HTMLDivElement>,
    clickStartedOutsideComponent?: boolean,
  ) => any;
  children: ReactNode;
  className?: string;
  clickEventType?: string;
  id?: string;
};

const OnClickElsewhere = (props: Props) => {
  const {
    handleClick,
    className,
    children,
    clickEventType,
    onClickInside,
    id,
  } = props;
  const clickStartedOutsideComponentRef = useRef(false);
  const ref = useRef<HTMLDivElement>(null);

  const onMouseDown = useCallback((event: MouseEvent) => {
    clickStartedOutsideComponentRef.current = !(
      ref.current && !ref.current.contains(event.target as Node)
    );
  }, []);

  const handleClickOutside = useCallback(
    event => {
      if (ref.current && !ref.current.contains(event.target) && handleClick) {
        // Don't move this one to the top if statement or onClickInside will not work correctly anymore
        if (
          !hasParentWithSelector(
            event.target as any,
            '.react-tiny-popover-container',
          )
        ) {
          handleClick(event, clickStartedOutsideComponentRef.current);
        }
      } else if (onClickInside) {
        onClickInside(event);
      }

      clickStartedOutsideComponentRef.current = false;
    },
    [handleClick, onClickInside],
  );

  useEffect(() => {
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener(
      clickEventType || 'click',
      handleClickOutside,
      true,
    );

    return () => {
      window.removeEventListener(
        clickEventType || 'click',
        handleClickOutside,
        true,
      );
      window.removeEventListener('mousedown', onMouseDown, true);
    };
  }, [clickEventType, handleClickOutside]);

  return (
    <div className={className} ref={ref} id={id}>
      {children}
    </div>
  );
};

export { OnClickElsewhere };
